import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Spacer } from 'app/layouts/generic';
import { Button } from 'app/shared/button';
import { DetailItem } from 'app/shared/info-section/item';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import { MultiSelect } from 'app/shared/select';
import { useIssueService } from 'hooks/api/agents/issue';
import { useIssueReportService } from 'hooks/api/agents/issue-report';
import { CATEGORY_OPTIONS, SEVERITY_OPTIONS } from '../constants';
import { assigneeFromUser, reporterFromTenant } from './context';
import styles from '../issues.module.css';

const NEW_ISSUE = { title: '', category: 'other', severity: 'medium', jira_key: '', jira_url: '' };

export const AddIssueToTenantDialog = ({ tenant, onAdded = () => {}, closeModal = () => {} }) => {
  const user_data = useSelector((store) => store.user_data);
  const { fetchIssues, createIssue } = useIssueService();
  const { createIssueReport } = useIssueReportService();

  const [mode, setMode] = useState('existing');
  const [issues, setIssues] = useState([]);
  const [issue_option, setIssueOption] = useState(null);
  const [new_issue, setNewIssue] = useState(NEW_ISSUE);

  const prefill = useMemo(() => reporterFromTenant(tenant), [tenant]);
  const [reporter_name, setReporterName] = useState(prefill.name);
  const [reporter_email, setReporterEmail] = useState(prefill.email);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchIssues({ query_string: 'sort_by=-last_reported_at&population=100' }).then(
      ({ issues: fetched }) => setIssues(fetched || [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const issue_options = useMemo(
    () =>
      issues.map((issue) => ({
        value: String(issue.id || issue._id),
        label: `${issue.title}${issue.reports_count ? ` · ${issue.reports_count} reports` : ''}`,
        issue
      })),
    [issues]
  );

  const assignee = useMemo(() => assigneeFromUser(user_data), [user_data]);
  const setNew = (field, value) => setNewIssue((prev) => ({ ...prev, [field]: value }));

  const can_submit =
    !submitting && (mode === 'existing' ? !!issue_option : new_issue.title.trim() !== '');

  const submitReport = async (issue_id) => {
    return createIssueReport({
      issue_id,
      tenant: { id: tenant.id || tenant._id, name: tenant.name || '' },
      reporter:
        reporter_name.trim() || reporter_email.trim()
          ? { name: reporter_name.trim(), email: reporter_email.trim() }
          : undefined,
      details: details.trim() || undefined,
      assignee
    });
  };

  const handleSubmit = async () => {
    if (!can_submit) return;
    setSubmitting(true);

    let issue_id = issue_option?.issue?.id || issue_option?.issue?._id;
    if (mode === 'new') {
      const { issue } = await createIssue({
        title: new_issue.title.trim(),
        category: new_issue.category,
        severity: new_issue.severity,
        jira_key: new_issue.jira_key.trim() || null,
        jira_url: new_issue.jira_url.trim() || null
      });
      if (!issue || !(issue.id || issue._id)) {
        setSubmitting(false);
        return;
      }
      issue_id = issue.id || issue._id;
    }

    const { report } = await submitReport(issue_id);
    setSubmitting(false);
    if (report) {
      onAdded(report);
      closeModal();
    }
  };

  return (
    <div className={styles.form}>
      <p className={styles.dialogSubtitle}>
        Add an issue to <strong>{tenant.name}</strong>.
      </p>

      <div className={styles.modeToggle}>
        <button
          type="button"
          className={`${styles.modeBtn} ${mode === 'existing' ? styles.modeActive : ''}`}
          onClick={() => setMode('existing')}
        >
          Link existing
        </button>
        <button
          type="button"
          className={`${styles.modeBtn} ${mode === 'new' ? styles.modeActive : ''}`}
          onClick={() => setMode('new')}
        >
          Create new
        </button>
      </div>

      <Spacer multiple={2} />

      {mode === 'existing' ? (
        <DetailItem title="Issue" required>
          <MultiSelect
            options={issue_options}
            value={issue_option}
            onChange={setIssueOption}
            isMulti={false}
            isClearable
            placeholder="Search existing issues…"
            menuPortalTarget={document.body}
          />
        </DetailItem>
      ) : (
        <>
          <p className={styles.dupeHint}>
            Search existing issues first — link to one rather than create a near-duplicate.
          </p>
          <DetailItem title="Title" required>
            <SimpleInput
              value={new_issue.title}
              onInput={(value) => setNew('title', value)}
              placeholder="Emails stuck in queue"
            />
          </DetailItem>
          <Spacer multiple={2} />
          <div className={styles.dialogGrid2}>
            <DetailItem title="Category">
              <MultiSelect
                options={CATEGORY_OPTIONS}
                value={CATEGORY_OPTIONS.find((o) => o.value === new_issue.category)}
                onChange={(option) => setNew('category', option.value)}
                isMulti={false}
                isClearable={false}
                menuPortalTarget={document.body}
              />
            </DetailItem>
            <DetailItem title="Severity">
              <MultiSelect
                options={SEVERITY_OPTIONS}
                value={SEVERITY_OPTIONS.find((o) => o.value === new_issue.severity)}
                onChange={(option) => setNew('severity', option.value)}
                isMulti={false}
                isClearable={false}
                menuPortalTarget={document.body}
              />
            </DetailItem>
          </div>
          <Spacer multiple={2} />
          <div className={styles.dialogGrid2}>
            <DetailItem title="Jira key">
              <SimpleInput
                value={new_issue.jira_key}
                onInput={(value) => setNew('jira_key', value)}
                placeholder="SUP-123"
              />
            </DetailItem>
            <DetailItem title="Jira URL">
              <SimpleInput
                value={new_issue.jira_url}
                onInput={(value) => setNew('jira_url', value)}
                placeholder="https://org.atlassian.net/browse/SUP-123"
              />
            </DetailItem>
          </div>
        </>
      )}

      <Spacer multiple={2} />

      <div className={styles.dialogGrid2}>
        <DetailItem title="Reporter name">
          <SimpleInput value={reporter_name} onInput={setReporterName} placeholder="Ada Obi" />
        </DetailItem>
        <DetailItem title="Reporter email">
          <SimpleInput
            value={reporter_email}
            onInput={setReporterEmail}
            placeholder="ada@acme.co"
          />
        </DetailItem>
      </div>

      <Spacer multiple={2} />

      <DetailItem title="Details">
        <TextArea
          rows={3}
          value={details}
          onInput={setDetails}
          placeholder="What did they report?"
        />
      </DetailItem>

      {assignee?.email && (
        <p className={styles.assigneeHint}>
          Assigned to{' '}
          {[assignee.firstname, assignee.lastname].filter(Boolean).join(' ') || assignee.email}
        </p>
      )}

      <Spacer multiple={3} />

      <div className={styles.dialogActions}>
        <Button text="Cancel" type="secondary" onClick={closeModal} />
        <Button
          text={mode === 'new' ? 'Create & add' : 'Add to tenant'}
          onClick={handleSubmit}
          disabled={!can_submit}
          loading={submitting}
        />
      </div>
    </div>
  );
};

export default AddIssueToTenantDialog;
